/* What the generator has cost, and what is actually in effect.
   OWNED BY ITEM 6 OF docs/plan/00-prestavba-2026-08.md.

   Two questions an author could not answer before: how much has gone, and
   whether the variable they changed on the platform has taken hold.
   Configuration is read once at start-up, so "set" and "in effect" are
   different questions — and on the day of a workshop that is the whole
   question. */

import { useCallback, useEffect, useState } from "react";
import { api, type Usage } from "../../lib/api";
import { useI18n } from "../../lib/i18n";

function money(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

function Meter({ label, value, max, text }: {
  label: string; value: number; max: number; text: string;
}) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="adm-meter">
      <div className="adm-meter-head">
        <span>{label}</span>
        <strong>{text}</strong>
      </div>
      {/* A real proportion of a real ceiling — unlike the generator's own
          progress, where the length of the answer is not known in advance. */}
      <div className="adm-meter-bar"><i style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export function Spend() {
  const { ui } = useI18n();
  const [usage, setUsage] = useState<Usage | null>(null);

  const load = useCallback(() => { void api<Usage>("/admin/usage").then(setUsage).catch(() => {}); }, []);
  useEffect(() => {
    load();
    // A workshop moves while this page is open; ten seconds is often enough to
    // watch a room work through its budget without hammering the database.
    const timer = window.setInterval(load, 10_000);
    return () => window.clearInterval(timer);
  }, [load]);

  if (!usage) return null;
  const { limits, effective } = usage;

  return (
    <section className="adm-panel">
      <div className="adm-meters">
        <Meter label={ui("spend.today")} value={usage.today_cost_usd} max={limits.daily_cost_usd}
               text={`${money(usage.today_cost_usd)} / ${money(limits.daily_cost_usd)}`} />
        <Meter label={ui("spend.runs")} value={usage.today_runs} max={limits.daily_max}
               text={`${usage.today_runs} / ${limits.daily_max}`} />
        <Meter label={ui("spend.live")} value={usage.live} max={limits.concurrent_max}
               text={`${usage.live} / ${limits.concurrent_max}`} />
      </div>

      <p className="adm-sub">
        {ui("spend.month")} <strong>{usage.month_runs}×</strong> · <strong>{money(usage.month_cost_usd)}</strong>
      </p>

      <h3 className="adm-h">{ui("spend.effective")}</h3>
      <ul className="adm-facts">
        <li><span>{ui("spend.model")}</span><code>{effective.model}</code></li>
        <li><span>{ui("spend.effort")}</span><code>{effective.effort}</code></li>
        <li>
          <span>{ui("spend.fast")}</span>
          {/* Must stay off unless the account really has fast-mode capacity:
              with none, the API refuses every request with a rate limit of
              zero, which reads as "busy" and fails identically on retry. */}
          <code className={effective.fast ? "warn" : ""}>
            {effective.fast ? ui("admin.on") : ui("admin.off")}
          </code>
        </li>
        <li>
          <span>{ui("spend.key")}</span>
          <code className={effective.key ? "" : "warn"}>
            {effective.key ? ui("spend.keySet") : ui("spend.keyMissing")}
          </code>
        </li>
      </ul>

      <h3 className="adm-h">{ui("spend.recent")}</h3>
      {usage.recent.length === 0 ? (
        <p className="adm-empty">{ui("spend.none")}</p>
      ) : (
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>{ui("spend.when")}</th><th>{ui("spend.who")}</th>
                <th>{ui("spend.status")}</th><th>{ui("spend.tokens")}</th>
                <th>{ui("spend.seconds")}</th><th>{ui("spend.cost")}</th>
              </tr>
            </thead>
            <tbody>
              {usage.recent.map((r, i) => (
                <tr key={i}>
                  <td>{r.at.replace("T", " ").slice(5, 16)}</td>
                  <td>{r.username}</td>
                  <td className={r.status === "ok" ? "" : "warn"}>{ui(`status.${r.status}`)}</td>
                  <td>{r.output_tokens.toLocaleString("cs")}</td>
                  <td>{r.seconds ?? "—"}</td>
                  <td>{money(r.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
